/**
 * Static option lists for the Customer create wizard, ported from
 * datacentral-client-v4 `modules/Customer/constants` + `Marketing/types/utils`.
 * Values are the backend enum keys (kept verbatim so `POST /customers` is hit
 * unchanged); labels are EN-first per the console's copy convention.
 */

export interface Option {
  value: string;
  label: string;
}

/** `customer_industry` — legacy `INDUSTRY_OPTIONS` (the `others` value is excluded from forms). */
export const INDUSTRY_OPTIONS: Option[] = [
  { value: 'ecomerce', label: 'Ecommerce (TMĐT, Tiki, Lazada, Shopee, …)' },
  { value: 'education', label: 'Education (Giáo dục, Elearning, EdTech)' },
  { value: 'fintech', label: 'Fintech (Thanh toán, P2P Lending, Đầu tư)' },
  { value: 'cryptocurrentcy', label: 'Cryptocurrency (Sàn giao dịch, Blockchain)' },
  { value: 'logistics', label: 'Logistics & Transport (Vận chuyển, Giao nhận, Kho bãi)' },
  { value: 'manufacturing', label: 'Manufacturing (Sản xuất, FMCG, Công nghiệp)' },
  { value: 'healthcare', label: 'Healthcare (Dược phẩm, Bệnh viện, Y tế, Mỹ phẩm)' },
  { value: 'services', label: 'Services (Du lịch, Khách sạn, Tư vấn, Pháp lý)' },
  { value: 'multimedia', label: 'Multimedia (Tin tức, OTT, MXH, Quảng cáo)' },
  { value: 'technology', label: 'Technology (Phần mềm, Outsource, AI, SaaS)' },
  { value: 'game', label: 'Game (Web/App Game, Game NFT)' },
  { value: 'real-estate', label: 'Real Estate (PropTech, BĐS)' },
  { value: 'retail', label: 'Retail (Bán lẻ, Phân phối, Bán buôn)' },
  { value: 'energy', label: 'Energy (Năng lượng, Dầu khí, Điện nước)' },
  { value: 'agriculture', label: 'Agriculture (Nông nghiệp, Thuỷ sản, AgriTech)' },
  { value: 'construction', label: 'Construction (Xây dựng, Kiến trúc, Vật liệu)' },
  { value: 'insurance', label: 'Insurance (Bảo hiểm)' },
  { value: 'securities', label: 'Securities (Chứng khoán)' },
  { value: 'airlines', label: 'Airlines (Hàng không)' },
  { value: 'bank', label: 'Bank (Ngân hàng)' },
  { value: 'telecom', label: 'Telecom (Viễn thông, ISP)' },
  { value: 'goverment', label: 'Government (Cơ quan nhà nước)' },
  { value: 'automotive', label: 'Automotive (Ô tô, Xe máy, Phụ tùng)' },
  { value: 'media-entertainment', label: 'Media & Entertainment (Giải trí, Sự kiện, Phim ảnh)' },
  { value: 'food-beverage', label: 'F&B (Nhà hàng, Quán cà phê, Chuỗi đồ uống)' },
  { value: 'fashion', label: 'Fashion & Beauty (Thời trang, Làm đẹp)' },
  { value: 'hr-recruitment', label: 'HR & Recruitment (Nhân sự, Tuyển dụng)' },
  { value: 'legal', label: 'Legal (Luật, Công chứng, Pháp lý)' },
  { value: 'accounting-audit', label: 'Accounting & Audit (Kế toán, Kiểm toán)' },
  { value: 'ngo', label: 'NGO (Tổ chức phi lợi nhuận, Hiệp hội)' },
  { value: 'defense', label: 'Defense & Security (Quốc phòng, An ninh)' },
  { value: 'sports-fitness', label: 'Sports & Fitness (Thể thao, Gym, Wellness)' },
  { value: 'pet', label: 'Pet & Veterinary (Thú cưng, Thú y)' },
  { value: 'environment', label: 'Environment (Môi trường, Xử lý rác thải)' },
  { value: 'printing-packaging', label: 'Printing & Packaging (In ấn, Bao bì)' },
];

/**
 * `customer_source`. Legacy `marketing_approach` is filtered out of the create
 * form (kept only for legacy records). `event` / `sale_event` reveal the
 * "event name" text field.
 */
export const CUSTOMER_SOURCES: Option[] = [
  { value: 'form', label: 'Web form' },
  { value: 'event', label: 'Event' },
  { value: 'livechat', label: 'Live chat' },
  { value: 'hotline', label: 'Hotline' },
  { value: 'email', label: 'Email' },
  { value: 'gg_ads', label: 'Google Ads' },
  { value: 'fb_ads', label: 'Facebook Ads' },
  { value: 'research', label: 'Research' },
  { value: 'contact', label: 'Direct contact' },
  { value: 'sale_event', label: 'Sales event' },
];

/** Sources that require an accompanying event name. */
export const EVENT_SOURCES = new Set(['event', 'sale_event']);

/** `customer_source_type`. */
export const CUSTOMER_SOURCE_TYPES: Option[] = [
  { value: 'internal', label: 'Internal' },
  { value: 'external', label: 'External' },
];

/**
 * `customer_lead_type` — from Marketing `CustomerLeadTypes`. `UNCLASSIFIED` is
 * the create-form default (matches the legacy create contract) and stays first.
 */
export const CUSTOMER_LEAD_TYPES: Option[] = [
  { value: 'UNCLASSIFIED', label: 'Unclassified' },
  { value: 'warm-lead', label: 'Warm lead' },
  { value: 'sale-qualified-lead', label: 'Sales-qualified lead' },
  { value: 'cool-lead', label: 'Cool lead' },
  { value: 'un-qualified-lead', label: 'Unqualified lead' },
];

/** Default `customer_status` for a freshly created lead. */
export const DEFAULT_CUSTOMER_STATUS = 'qualified-prospect';
